import FadeIn from './FadeIn';

const gold = '#C8A96E';
const charcoal = '#1A1916';
const secondary = '#6B6862';

const pageLabels = {
  en: { home: 'Home', writings: 'Writings', about: 'About', infinity: 'Infinity', media: 'Media', contact: 'Contact' },
  es: { home: 'Inicio', writings: 'Escritos', about: 'Acerca de', infinity: 'Infinity', media: 'Medios', contact: 'Contacto' },
};

const SectionRule = () => (
  <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 72px' }} />
);

const Label = ({ children }) => (
  <p style={{ color: gold, fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.22em', textTransform: 'uppercase', marginBottom: '16px' }}>{children}</p>
);

const ThesisPage = ({ previousPage, setCurrentPage, language = 'en' }) => {
  const fromPage = previousPage || 'writings';
  const labels = pageLabels[language] || pageLabels.en;
  const fromLabel = labels[fromPage] || (language === 'es' ? 'Volver' : 'Back');


  const handleBack = () => {
    if (setCurrentPage) setCurrentPage(fromPage);
  };

  const t = {
    breadcrumb: language === 'es' ? 'Tesis' : 'Thesis',
    heroLabel: language === 'es' ? 'Tesis' : 'Thesis',
    heroTitle: language === 'es' ? 'La Tesis Infinity' : 'The Infinity Thesis',
    heroSub: language === 'es'
      ? 'Construyendo Sistemas de Largo Horizonte para el Futuro del Entorno Construido — y Más Allá.'
      : 'Building Long-Horizon Systems for the Future of the Built Environment — and Beyond.',

    s1Label: language === 'es' ? 'Apertura' : 'Opening',
    s1Paras: language === 'es' ? [
      'El mundo está entrando en un período de transformación estructural.',
      'Los sistemas de vivienda están bajo presión. La infraestructura envejece. La volatilidad climática aumenta. La tecnología se acelera más rápido de lo que las instituciones pueden adaptarse.',
      'Al mismo tiempo, el entorno construido sigue siendo una de las fuerzas más importantes que dan forma a la vida humana — influyendo en la oportunidad económica, el consumo de energía, la movilidad, la resiliencia y la calidad de vida a través de las generaciones.',
      'Infinity fue creado en respuesta a esa realidad.',
    ] : [
      'The world is entering a period of structural transformation.',
      'Housing systems are under pressure. Infrastructure is aging. Climate volatility is increasing. Technology is accelerating faster than institutions can adapt.',
      'At the same time, the built environment remains one of the most consequential forces shaping human life — influencing economic opportunity, energy consumption, mobility, resilience, and quality of life across generations.',
      'Infinity was created in response to that reality.',
    ],

    s2Label: language === 'es' ? 'El Punto de Partida' : 'The Starting Point',
    s2Title: language === 'es' ? 'Por Qué el Entorno Construido' : 'Why the Built Environment',
    s2p1: language === 'es'
      ? 'Creemos que el entorno construido es el punto de partida lógico para el cambio de sistemas a largo plazo.'
      : 'We believe the built environment is the logical starting point for long-term systems change.',
    s2p2: language === 'es' ? 'Los bienes raíces se encuentran en la intersección de:' : 'Real estate sits at the intersection of:',
    s2List: language === 'es'
      ? ['capital', 'energía', 'infraestructura', 'tecnología', 'y comportamiento humano']
      : ['capital', 'energy', 'infrastructure', 'technology', 'and human behavior'],
    s2p3: language === 'es'
      ? 'Es una de las pocas industrias capaces de influir en los resultados económicos, ambientales y sociales simultáneamente.'
      : 'It is one of the few industries capable of influencing economic, environmental, and social outcomes simultaneously.',
    s2p4: language === 'es'
      ? 'Infinity Development sirve como el motor de ejecución de esta visión hoy — enfocado en plataformas sostenibles del sector de vivienda en Estados Unidos y América Latina.'
      : 'Infinity Development serves as the execution engine of this vision today — focused on sustainable living-sector platforms across the United States and Latin America.',

    s3Label: language === 'es' ? 'Creencia Central' : 'Core Belief',
    s3Title: language === 'es' ? 'El Principio Organizador' : 'The Organizing Principle',
    s3p1: language === 'es' ? 'Nuestra creencia es simple:' : 'Our belief is simple:',
    s3p2: language === 'es'
      ? 'El futuro pertenecerá a las organizaciones capaces de integrar:'
      : 'The future will belong to organizations capable of integrating:',
    s3List: language === 'es'
      ? ['gobernanza institucional', 'innovación técnica', 'y administración de largo horizonte']
      : ['institutional governance', 'technical innovation', 'and long-horizon stewardship'],
    s3p3: language === 'es' ? 'en un único marco operativo.' : 'into a single operating framework.',
    s3p4: language === 'es' ? 'No empresas aisladas. Sistemas integrados.' : 'Not isolated companies. Integrated systems.',

    s4Label: language === 'es' ? 'Sistema Operativo Interno' : 'Internal Operating System',
    s4Title: 'Infinity Cortex',
    s4p1: language === 'es'
      ? 'Para respaldar este enfoque, Infinity está construyendo Infinity Cortex — un sistema operativo interno diseñado para mejorar cómo se buscan oportunidades, se mide el riesgo, se entregan proyectos y se rastrea el desempeño a lo largo del tiempo.'
      : 'To support this approach, Infinity is building Infinity Cortex — an internal operating system designed to improve how opportunities are sourced, risk is measured, projects are delivered, and performance is tracked over time.',
    s4p2: language === 'es'
      ? 'El objetivo no es la automatización por sí misma.'
      : 'The goal is not automation for its own sake.',
    s4p3: language === 'es' ? 'El objetivo es la ejecución disciplinada a escala.' : 'The goal is disciplined execution at scale.',

    s5Label: language === 'es' ? 'Dirección a Largo Plazo' : 'Long-Term Direction',
    s5Title: language === 'es' ? 'El Ecosistema' : 'The Ecosystem',
    s5p1: language === 'es'
      ? 'Con el tiempo, el ecosistema Infinity está diseñado para expandirse hacia sectores adyacentes donde la infraestructura, la innovación y el pensamiento sistémico a largo plazo se intersectan.'
      : 'Over time, the Infinity ecosystem is designed to expand into adjacent sectors where infrastructure, innovation, and long-term systems thinking intersect.',
    s5p2: language === 'es' ? 'Estos futuros verticales pueden incluir:' : 'These future verticals may include:',
    s5List: language === 'es' ? [
      'sistemas de energía',
      'infraestructura inteligente',
      'movilidad avanzada y aviación',
      'sistemas oceánicos y climáticos',
      'tecnologías de frontera',
      'e iniciativas filantrópicas de largo horizonte',
    ] : [
      'energy systems',
      'intelligent infrastructure',
      'advanced mobility and aviation',
      'ocean and climate systems',
      'frontier technologies',
      'and long-horizon philanthropic initiatives',
    ],
    s5p3: language === 'es'
      ? 'Cada expansión se perseguirá deliberadamente, con liderazgo independiente, disciplina operativa y alineación estratégica clara.'
      : 'Each expansion will be pursued deliberately, with independent leadership, operational discipline, and clear strategic alignment.',
    s5quote: language === 'es'
      ? 'Infinity no se está construyendo para una expansión rápida. Se está construyendo para la durabilidad.'
      : 'Infinity is not being built for rapid expansion. It is being built for durability.',

    s6Label: language === 'es' ? 'Convicción' : 'Conviction',
    s6Title: language === 'es' ? 'Por Qué Largo Horizonte' : 'Why Long-Horizon',
    s6Paras: language === 'es' ? [
      'Mi creencia en la construcción de largo horizonte proviene de la comprensión de que los sistemas significativos rara vez se crean rápidamente.',
      'Se construyen con paciencia — a través de la disciplina, la resiliencia, la iteración y la responsabilidad.',
      'Infinity no es un proyecto a corto plazo.',
      'Es un compromiso de varias décadas para construir sistemas que perduren económica, ambiental e institucionalmente.',
    ] : [
      'My belief in long-horizon building comes from the understanding that meaningful systems are rarely created quickly.',
      'They are built patiently — through discipline, resilience, iteration, and responsibility.',
      'Infinity is not a short-term project.',
      'It is a multi-decade commitment to building systems that endure economically, environmentally, and institutionally.',
    ],

    s7p1: language === 'es'
      ? 'El futuro no será moldeado solo por ideas aisladas.'
      : 'The future will not be shaped by isolated ideas alone.',
    s7p2: language === 'es'
      ? 'Será moldeado por los sistemas, instituciones e infraestructura que elijamos construir — y la disciplina con la que los construimos.'
      : 'It will be shaped by the systems, institutions, and infrastructure we choose to build — and the discipline with which we build them.',
    s7p3: language === 'es'
      ? 'Infinity existe para contribuir significativamente a ese futuro.'
      : 'Infinity exists to contribute meaningfully to that future.',
    s7sign: language === 'es' ? 'Hacia el Infinito y Más Allá,' : 'To Infinity and Beyond,',
  };

  return (
    <div className="min-h-screen bg-white">

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section className="px-6 text-center" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>

        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '32px', justifyContent: 'center' }}>
          <button
            onClick={handleBack}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: secondary, fontSize: '0.72rem', fontWeight: 400, letterSpacing: '0.08em', transition: 'color 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.color = gold}
            onMouseLeave={e => e.currentTarget.style.color = secondary}
          >
            {fromLabel}
          </button>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
            <path d="M9 18l6-6-6-6" />
          </svg>
          <span style={{ color: gold, fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.08em' }}>{t.breadcrumb}</span>
        </div>

        <Label>{t.heroLabel}</Label>
        <h1 className="font-light" style={{ color: charcoal, fontSize: 'clamp(2.8rem, 8vw, 5.5rem)', lineHeight: '1.05', letterSpacing: '-0.025em', marginBottom: '28px' }}>
          {t.heroTitle}
        </h1>
        <p className="font-light" style={{ color: secondary, fontSize: '1rem', lineHeight: '1.9', maxWidth: '520px', margin: '0 auto 12px' }}>
          {t.heroSub}
        </p>
        <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '24px auto 0' }} />
        <div className="scroll-arrow" style={{ position: 'absolute', bottom: '40px', left: '50%', transform: 'translateX(-50%)' }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 3v14M3 10l7 7 7-7" stroke={gold} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </section>

      {/* ─── SECTION 1 — OPENING ──────────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{t.s1Label}</Label>
            {t.s1Paras.map((para, i) => (
              <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 2 — WHY THE BUILT ENVIRONMENT ───────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{t.s2Label}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {t.s2Title}
            </h2>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s2p1}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '24px' }}>{t.s2p2}</p>
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginBottom: '32px' }}>
              {t.s2List.map((item, i) => (
                <p key={i} className="font-light text-sm" style={{ color: charcoal, lineHeight: '2.4' }}>— {item}</p>
              ))}
            </div>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2' }}>{t.s2p3}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginTop: '20px' }}>{t.s2p4}</p>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 3 — CORE BELIEF ──────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{t.s3Label}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {t.s3Title}
            </h2>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s3p1}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '24px' }}>{t.s3p2}</p>
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginBottom: '32px' }}>
              {t.s3List.map((item, i) => (
                <p key={i} className="font-light text-sm" style={{ color: charcoal, lineHeight: '2.4' }}>— {item}</p>
              ))}
            </div>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s3p3}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2' }}>{t.s3p4}</p>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 4 — INFINITY CORTEX ─────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{t.s4Label}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {t.s4Title}
            </h2>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s4p1}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s4p2}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2' }}>{t.s4p3}</p>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 5 — THE ECOSYSTEM ───────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{t.s5Label}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {t.s5Title}
            </h2>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s5p1}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '24px' }}>{t.s5p2}</p>
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginBottom: '32px' }}>
              {t.s5List.map((item, i) => (
                <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2.4' }}>— {item}</p>
              ))}
            </div>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s5p3}</p>
            <div style={{ borderLeft: `2px solid ${gold}`, paddingLeft: '24px', marginTop: '32px' }}>
              <p className="font-light text-sm" style={{ color: charcoal, lineHeight: '2' }}>{t.s5quote}</p>
            </div>
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 6 — CONVICTION ───────────────────────────── */}
      <section className="py-20 px-6">
        <FadeIn>
          <div style={{ maxWidth: '560px', margin: '0 auto' }}>
            <Label>{t.s6Label}</Label>
            <h2 className="font-light" style={{ color: charcoal, fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', lineHeight: '1.2', marginBottom: '32px' }}>
              {t.s6Title}
            </h2>
            {t.s6Paras.map((para, i) => (
              <p key={i} className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{para}</p>
            ))}
          </div>
        </FadeIn>
      </section>

      <SectionRule />

      {/* ─── SECTION 7 — CLOSING ─────────────────────────────── */}
      <section className="py-20 px-6 text-center">
        <FadeIn>
          <div style={{ maxWidth: '480px', margin: '0 auto' }}>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '20px' }}>{t.s7p1}</p>
            <p className="font-light text-sm" style={{ color: secondary, lineHeight: '2', marginBottom: '40px' }}>{t.s7p2}</p>
            <p className="font-light text-sm" style={{ color: charcoal, lineHeight: '2', marginBottom: '40px' }}>{t.s7p3}</p>
            <div style={{ width: '32px', height: '1px', backgroundColor: gold, margin: '0 auto 32px' }} />
            <p className="font-light" style={{ color: secondary, fontSize: '0.85rem', lineHeight: '2', fontStyle: 'italic' }}>{t.s7sign}</p>
            <p className="font-light" style={{ color: charcoal, fontSize: '0.85rem', lineHeight: '2', letterSpacing: '0.06em' }}>Zeus Hernandez</p>
          </div>
        </FadeIn>
      </section>

    </div>
  );
};

export default ThesisPage;
